import React, { useMemo, useState, useCallback, ReactNode } from 'react';
import {
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  flexRender,
  SortingState,
  ColumnFiltersState,
  PaginationState,
  RowSelectionState,
  VisibilityState,
  ColumnDef,
  Row,
  Updater,
  Column,
} from '@tanstack/react-table';
import IconSystem from 'icon-collection/icon-systems';
import { ActionMenu, ActionMenuItem } from '../ActionMenu';

// ============================================================================
// INJECT SELF-CONTAINED STYLES
// ============================================================================

if (typeof document !== 'undefined') {
  const styleId = 'syut-grid-styles';
  if (!document.getElementById(styleId)) {
    const style = document.createElement('style');
    style.id = styleId;
    style.textContent = `
      /* SyutGrid Component Styles */
      .syut-grid-component { display: flex; flex-direction: column; height: 100%; font-family: inherit; }
      .syut-grid-wrapper { flex: 1; overflow: auto; min-height: 0; border: 1px solid var(--border-color, #e2e8f0); border-radius: 8px 8px 0 0; background: var(--bg-primary, #ffffff); }
      .syut-grid { width: 100%; border-collapse: collapse; font-size: var(--font-size-base, 0.875rem); background: var(--bg-primary, #ffffff); }
      .syut-grid thead { position: sticky; top: 0; z-index: 3; }
      .syut-grid th { padding: 0.75rem 1rem; text-align: left; font-weight: 600; color: var(--text-primary, #1e293b); background: var(--grid-header-bg, #dbeafe); border-bottom: 2px solid var(--border-color, #e2e8f0); white-space: nowrap; user-select: none; }
      .syut-grid th.frozen-column { background: var(--grid-header-bg, #dbeafe); box-shadow: -2px 0 4px rgba(0, 0, 0, 0.1); z-index: 4; }
      .syut-grid td.frozen-column { z-index: 1; }

      .syut-grid td {
        padding: 0.625rem 1rem;
        color: var(--text-primary, #1e293b);
        border-bottom: 1px solid var(--border-color, #e2e8f0);
        background: var(--bg-primary, #ffffff);
      }

      .syut-grid td.frozen-column {
        background: var(--bg-primary, #ffffff);
        box-shadow: 2px 0 4px rgba(0, 0, 0, 0.1);
      }

      .syut-grid tbody tr:hover td {
        background: var(--bg-secondary, #f8fafc);
      }

      .syut-grid tbody tr:hover td.frozen-column {
        background: var(--bg-secondary, #f8fafc);
      }

      .syut-grid th.action-column,
      .syut-grid td.action-column {
        text-align: center;
        width: 80px;
      }

      .syut-grid .row-select,
      .syut-grid .row-select-all {
        width: 16px;
        height: 16px;
        cursor: pointer;
        accent-color: var(--primary, #3b82f6);
      }

      /* Pagination Footer */
      .syut-grid-component .pagination-footer {
        display: flex;
        align-items: center;
        justify-content: space-between;
        padding: 0.75rem 1rem;
        background: var(--bg-secondary, #f8fafc);
        border: 1px solid var(--border-color, #e2e8f0);
        border-top: none;
        border-radius: 0 0 8px 8px;
        flex-shrink: 0;
      }

      .syut-grid-component .pagination-info {
        font-size: var(--font-size-sm, 0.8125rem);
        color: var(--text-muted, #64748b);
      }

      .syut-grid-component .pagination-controls {
        display: flex;
        align-items: center;
        gap: 0.5rem;
      }

      .syut-grid-component .pagination-controls select {
        padding: 0.375rem 0.5rem;
        border-radius: 0.375rem;
        border: 1px solid var(--border-color, #e2e8f0);
        font-size: var(--font-size-sm, 0.8125rem);
        background: var(--bg-primary, #ffffff);
        color: var(--text-primary, #1e293b);
        cursor: pointer;
      }

      .syut-grid-component .page-btn {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        padding: 0.375rem 0.5rem;
        border: 1px solid var(--border-color, #e2e8f0);
        border-radius: 0.375rem;
        background: var(--bg-primary, #ffffff);
        color: var(--text-primary, #1e293b);
        cursor: pointer;
        transition: all 0.15s ease;
      }

      .syut-grid-component .page-btn:hover:not(:disabled) {
        background: var(--bg-secondary, #f8fafc);
        border-color: var(--primary, #3b82f6);
        color: var(--primary, #3b82f6);
      }

      .syut-grid-component .page-btn:disabled {
        opacity: 0.5;
        cursor: not-allowed;
      }
    `;
    document.head.appendChild(style);
  }
}

// ============================================================================
// TYPES
// ============================================================================

export interface SyutGridColumn<T> {
  id: string;
  header: string | ((column: any) => ReactNode);
  accessorKey?: keyof T;
  cell?: (info: { row: Row<T>; getValue: () => any }) => ReactNode;
  size?: number;
  enableSorting?: boolean;
  enableFiltering?: boolean;
  /** Freeze this column (sticky positioning) - 'left' or 'right' */
  frozen?: 'left' | 'right';
}

export interface SyutGridProps<T extends { id: string }> {
  /** Data to display in the table */
  data: T[];
  /** Column definitions */
  columns: SyutGridColumn<T>[];
  /** Enable row selection */
  enableRowSelection?: boolean;
  /** Enable pagination */
  enablePagination?: boolean;
  /** Page size options */
  pageSizeOptions?: number[];
  /** Default page size */
  defaultPageSize?: number;
  /** Enable sorting */
  enableSorting?: boolean;
  /** Enable column filtering (shows filter inputs in header) */
  enableColumnFilters?: boolean;
  /** Action menu items for each row */
  rowActions?: (row: T) => ActionMenuItem[];
  /** Custom empty state message */
  emptyMessage?: string;
  /** Callback when row selection changes */
  onSelectionChange?: (selectedRows: T[]) => void;
  /** Loading state */
  isLoading?: boolean;
  /** Additional class name */
  className?: string;
  /** Column IDs to freeze on left side (in order) */
  frozenLeftColumns?: string[];
  /** Column IDs to freeze on right side (in order) */
  frozenRightColumns?: string[];
  /** Visible column IDs - controls which columns are shown */
  visibleColumns?: string[];
}

// Helper type for tracking frozen column positions
interface FrozenColumnInfo {
  frozen: 'left' | 'right' | null;
  leftOffset: number;
  rightOffset: number;
}

// ============================================================================
// COLUMN FILTER COMPONENT
// ============================================================================

interface ColumnFilterProps<T> {
  column: Column<T, unknown>;
  placeholder?: string;
}

function ColumnFilter<T>({ column, placeholder }: ColumnFilterProps<T>) {
  const columnFilterValue = column.getFilterValue() as string;

  return (
    <input
      type="text"
      value={columnFilterValue ?? ''}
      onChange={(e) => column.setFilterValue(e.target.value)}
      placeholder={placeholder || 'Filter...'}
      onClick={(e) => e.stopPropagation()}
      style={{
        width: '100%',
        padding: '0.25rem 0.5rem',
        fontSize: '0.75rem',
        border: '1px solid var(--border-color)',
        borderRadius: '0.25rem',
        marginTop: '0.375rem',
        background: 'var(--bg-primary)',
      }}
    />
  );
}

// ============================================================================
// MAIN COMPONENT
// ============================================================================

/**
 * SyutGrid - A reusable data grid component built on TanStack Table
 *
 * @example
 * ```tsx
 * <SyutGrid
 *   data={users}
 *   columns={[
 *     { id: 'name', header: 'Name', accessorKey: 'name' },
 *     { id: 'email', header: 'Email', accessorKey: 'email' },
 *   ]}
 *   enableColumnFilters
 *   rowActions={(user) => [
 *     { id: 'edit', label: 'Edit', onClick: () => handleEdit(user) },
 *     { id: 'delete', label: 'Delete', onClick: () => handleDelete(user), variant: 'danger' },
 *   ]}
 * />
 * ```
 */
export function SyutGrid<T extends { id: string }>({
  data,
  columns,
  enableRowSelection = false,
  enablePagination = true,
  pageSizeOptions = [10, 25, 50, 100],
  defaultPageSize = 10,
  enableSorting = true,
  enableColumnFilters = false,
  rowActions,
  emptyMessage = 'No data found',
  onSelectionChange,
  isLoading = false,
  className = '',
  frozenLeftColumns = [],
  frozenRightColumns = [],
  visibleColumns,
}: SyutGridProps<T>) {
  // Table state
  const [sorting, setSorting] = useState<SortingState>([]);
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);
  const [rowSelection, setRowSelection] = useState<RowSelectionState>({});
  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: defaultPageSize,
  });

  // Calculate column visibility from visibleColumns prop
  // If visibleColumns is not provided, all columns are visible
  const columnVisibility = useMemo<VisibilityState>(() => {
    if (!visibleColumns) {
      return {}; // All columns visible
    }

    // Build visibility state: columns not in visibleColumns are hidden
    const visibility: VisibilityState = {};

    // Get all column IDs (including select and actions)
    const allColumnIds = [
      ...(enableRowSelection ? ['select'] : []),
      ...columns.map(col => col.id),
      ...(rowActions ? ['actions'] : []),
    ];

    allColumnIds.forEach(colId => {
      visibility[colId] = visibleColumns.includes(colId);
    });

    return visibility;
  }, [visibleColumns, columns, enableRowSelection, rowActions]);

  // Compute frozen column information based on frozenLeftColumns and frozenRightColumns
  const frozenColumnMap = useMemo(() => {
    const map: Record<string, FrozenColumnInfo> = {};

    // Helper to get column width by id
    const getColumnWidth = (colId: string): number => {
      if (colId === 'select') return 50;
      if (colId === 'actions') return 80;
      const col = columns.find(c => c.id === colId);
      return col?.size || 150;
    };

    // Calculate left frozen columns (in order provided)
    let leftOffset = 0;
    frozenLeftColumns.forEach((colId) => {
      map[colId] = { frozen: 'left', leftOffset, rightOffset: 0 };
      leftOffset += getColumnWidth(colId);
    });

    // Calculate right frozen columns (in order provided, rightmost first)
    let rightOffset = 0;
    // Reverse the array so the last item in frozenRightColumns is at the rightmost position
    [...frozenRightColumns].reverse().forEach((colId) => {
      map[colId] = { frozen: 'right', leftOffset: 0, rightOffset };
      rightOffset += getColumnWidth(colId);
    });

    return map;
  }, [columns, frozenLeftColumns, frozenRightColumns]);

  // Helper function to get sticky styles for a column
  const getStickyStyles = useCallback((columnId: string): React.CSSProperties => {
    const frozenInfo = frozenColumnMap[columnId];
    if (!frozenInfo || !frozenInfo.frozen) {
      return {};
    }

    return {
      position: 'sticky',
      [frozenInfo.frozen]: frozenInfo.frozen === 'left' ? frozenInfo.leftOffset : frozenInfo.rightOffset,
      zIndex: 1,
    };
  }, [frozenColumnMap]);

  // Build TanStack Table columns
  const tableColumns = useMemo(() => {
    const cols: ColumnDef<T, any>[] = [];

    // Add selection column if enabled
    if (enableRowSelection) {
      cols.push({
        id: 'select',
        header: ({ table }) => (
          <input
            type="checkbox"
            className="row-select-all"
            checked={table.getIsAllRowsSelected()}
            onChange={table.getToggleAllRowsSelectedHandler()}
          />
        ),
        cell: ({ row }) => (
          <input
            type="checkbox"
            className="row-select"
            checked={row.getIsSelected()}
            onChange={row.getToggleSelectedHandler()}
          />
        ),
        size: 50,
        enableColumnFilter: false,
      });
    }

    // Add data columns
    columns.forEach((col) => {
      const canFilter = enableColumnFilters && col.enableFiltering !== false;
      const columnDef: ColumnDef<T, any> = {
        id: col.id,
        accessorKey: col.accessorKey,
        header: typeof col.header === 'string'
          ? ({ column }) => {
              const headerText = col.header as string;
              return (
                <div>
                  <div
                    style={{
                      cursor: enableSorting && col.enableSorting !== false ? 'pointer' : 'default',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '4px',
                    }}
                    onClick={() => enableSorting && col.enableSorting !== false && column.toggleSorting()}
                  >
                    <span>{headerText}</span>
                    {enableSorting && col.enableSorting !== false && (
                      <span>
                        {column.getIsSorted() === 'asc' ? (
                          <IconSystem.chevronUp size={14} />
                        ) : column.getIsSorted() === 'desc' ? (
                          <IconSystem.chevronDown size={14} />
                        ) : (
                          <IconSystem.chevronUp size={14} style={{ opacity: 0.3 }} />
                        )}
                      </span>
                    )}
                  </div>
                  {canFilter && (
                    <ColumnFilter column={column} placeholder={`Filter ${headerText}...`} />
                  )}
                </div>
              );
            }
          : col.header,
        cell: col.cell
          ? (info) => col.cell!({ row: info.row, getValue: info.getValue })
          : (info) => info.getValue(),
        size: col.size,
        enableSorting: enableSorting && col.enableSorting !== false,
        enableColumnFilter: canFilter,
      };
      cols.push(columnDef);
    });

    // Add actions column if rowActions is provided
    if (rowActions) {
      cols.push({
        id: 'actions',
        header: 'Actions',
        cell: ({ row }) => (
          <ActionMenu items={rowActions(row.original)} />
        ),
        size: 80,
        enableColumnFilter: false,
      });
    }

    return cols;
  }, [columns, enableRowSelection, enableSorting, enableColumnFilters, rowActions]);

  // Handle selection change
  const handleRowSelectionChange = useCallback((updaterOrValue: Updater<RowSelectionState>) => {
    setRowSelection((prev) => {
      const newSelection = typeof updaterOrValue === 'function'
        ? updaterOrValue(prev)
        : updaterOrValue;

      if (onSelectionChange) {
        const selectedRows = Object.keys(newSelection)
          .filter((key) => newSelection[key])
          .map((key) => data.find((row) => row.id === key))
          .filter(Boolean) as T[];
        onSelectionChange(selectedRows);
      }

      return newSelection;
    });
  }, [data, onSelectionChange]);

  // Create table instance
  const table = useReactTable({
    data,
    columns: tableColumns,
    state: {
      sorting,
      columnFilters,
      rowSelection,
      pagination,
      columnVisibility,
    },
    onSortingChange: setSorting,
    onColumnFiltersChange: setColumnFilters,
    onRowSelectionChange: handleRowSelectionChange,
    onPaginationChange: setPagination,
    // columnVisibility is controlled via visibleColumns prop, no internal setter needed
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: enablePagination ? getPaginationRowModel() : undefined,
    enableRowSelection,
    getRowId: (row) => row.id,
  });

  return (
    <div className={`syut-grid-component ${className}`} style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      {/* Table */}
      <div className="syut-grid-wrapper" style={{ flex: 1, overflow: 'auto', minHeight: 0 }}>
        <table className="syut-grid">
          <thead>
            {table.getHeaderGroups().map((headerGroup) => (
              <tr key={headerGroup.id}>
                {headerGroup.headers.map((header) => {
                  const stickyStyles = getStickyStyles(header.id);
                  const isFrozen = !!frozenColumnMap[header.id];
                  return (
                    <th
                      key={header.id}
                      style={{
                        width: header.getSize() !== 150 ? header.getSize() : undefined,
                        ...stickyStyles,
                        ...(isFrozen && { zIndex: 4 }),
                      }}
                      className={`${header.id === 'actions' ? 'action-column' : ''} ${isFrozen ? 'frozen-column' : ''}`}
                    >
                      {header.isPlaceholder
                        ? null
                        : flexRender(header.column.columnDef.header, header.getContext())}
                    </th>
                  );
                })}
              </tr>
            ))}
          </thead>
          <tbody>
            {isLoading ? (
              <tr>
                <td
                  colSpan={table.getAllColumns().length}
                  style={{ textAlign: 'center', padding: '2rem', color: 'var(--text-muted)' }}
                >
                  Loading...
                </td>
              </tr>
            ) : table.getRowModel().rows.length === 0 ? (
              <tr>
                <td
                  colSpan={table.getAllColumns().length}
                  style={{ textAlign: 'center', padding: '2rem', color: 'var(--text-muted)' }}
                >
                  {emptyMessage}
                </td>
              </tr>
            ) : (
              table.getRowModel().rows.map((row) => (
                <tr key={row.id}>
                  {row.getVisibleCells().map((cell) => {
                    const stickyStyles = getStickyStyles(cell.column.id);
                    const isFrozen = !!frozenColumnMap[cell.column.id];
                    const isActionsColumn = cell.column.id === 'actions';
                    return (
                      <td
                        key={cell.id}
                        className={`${isActionsColumn ? 'action-column' : ''} ${isFrozen ? 'frozen-column' : ''}`}
                        style={{
                          ...stickyStyles,
                          ...(isActionsColumn && { overflow: 'visible' }),
                        }}
                      >
                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                      </td>
                    );
                  })}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {enablePagination && (
        <div className="pagination-footer" style={{ flexShrink: 0 }}>
          <div className="pagination-info">
            Showing {table.getRowModel().rows.length} of {table.getFilteredRowModel().rows.length} items
            {Object.keys(rowSelection).length > 0 && (
              <span style={{ marginLeft: '1rem', color: 'var(--primary)' }}>
                ({Object.keys(rowSelection).length} selected)
              </span>
            )}
          </div>
          <div className="pagination-controls" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <select
              value={pagination.pageSize}
              onChange={(e) => table.setPageSize(Number(e.target.value))}
              style={{
                padding: '0.375rem 0.5rem',
                borderRadius: '0.375rem',
                border: '1px solid var(--border-color)',
                fontSize: '0.85rem',
                marginRight: '1rem',
              }}
            >
              {pageSizeOptions.map((size) => (
                <option key={size} value={size}>
                  {size} / page
                </option>
              ))}
            </select>
            <button
              className="page-btn"
              onClick={() => table.firstPage()}
              disabled={!table.getCanPreviousPage()}
            >
              <IconSystem.chevronLeft size={16} />
              <IconSystem.chevronLeft size={16} style={{ marginLeft: '-8px' }} />
            </button>
            <button
              className="page-btn"
              onClick={() => table.previousPage()}
              disabled={!table.getCanPreviousPage()}
            >
              <IconSystem.chevronLeft size={16} />
            </button>
            <span style={{ padding: '0 0.75rem', fontSize: '0.85rem' }}>
              Page {table.getState().pagination.pageIndex + 1} of {table.getPageCount()}
            </span>
            <button
              className="page-btn"
              onClick={() => table.nextPage()}
              disabled={!table.getCanNextPage()}
            >
              <IconSystem.chevronRight size={16} />
            </button>
            <button
              className="page-btn"
              onClick={() => table.lastPage()}
              disabled={!table.getCanNextPage()}
            >
              <IconSystem.chevronRight size={16} />
              <IconSystem.chevronRight size={16} style={{ marginLeft: '-8px' }} />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default SyutGrid;
